import * as XLSX from "xlsx"
import type { PlotlyGraph } from "@modules/data/components/data/chart/PlotlyChartCard.vue"

type PlotlyTrace = {
    name?: string
    x?: unknown[]
    y?: unknown[]
    text?: unknown[]
    type?: string
}

export type ChartExportOptions = {
    /** Slug du graphique (ex. `consommation-electricite`) — utilisé dans le nom de fichier. */
    chartSlug: string
    /** Slug bâtiment (ou id) pour traçabilité — inclus dans le nom de fichier si fourni. */
    buildingSlug?: string | null
    /** En-tête de la colonne de l'axe X. Default `Date`. */
    xAxisLabel?: string
    /** Préfixe pour traces sans `name`. Default `Série`. */
    fallbackTraceName?: string
    /** Nom de l'onglet quand on aligne sur un X commun. Default `Données`. */
    sheetName?: string
}

export type ChartExportRowsOptions = {
    chartSlug: string
    buildingSlug?: string | null
    sheetName?: string
}

export type WorkbookSheetSpec =
    | { kind: "rows"; name: string; rows: unknown[][] }
    | {
          kind: "chart"
          name: string
          graph: PlotlyGraph | null | undefined
          xAxisLabel?: string
          fallbackTraceName?: string
      }

export type WorkbookExportOptions = {
    /** Slug global utilisé dans le nom de fichier (ex. `synthese-financiere`). */
    chartSlug: string
    /** Slug bâtiment / nom analyse — inclus dans le nom de fichier si fourni. */
    buildingSlug?: string | null
}

function normalizeXValue(v: unknown): string | number {
    if (v == null) return ""
    if (v instanceof Date) return v.toISOString()
    if (typeof v === "number") return v
    return String(v)
}

function normalizeYValue(v: unknown): number | string | null {
    if (v == null) return null
    if (typeof v === "number") return Number.isFinite(v) ? v : null
    if (typeof v === "string") {
        const trimmed = v.trim()
        if (trimmed.length === 0) return null
        const n = Number(trimmed)
        return Number.isNaN(n) ? trimmed : n
    }
    return String(v)
}

function resolveTraceName(
    trace: PlotlyTrace,
    fallback: string,
    index: number
): string {
    if (typeof trace.name === "string" && trace.name.trim().length > 0) {
        return trace.name.trim()
    }
    return `${fallback} ${index + 1}`
}

function sanitizeSheetName(name: string): string {
    const cleaned = name
        .replace(/[\\/?*[\]:]/g, " ")
        .slice(0, 31)
        .trim()
    return cleaned.length > 0 ? cleaned : "Données"
}

function slugify(input: string): string {
    return input
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z0-9-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase()
}

function buildFilename(opts: {
    chartSlug: string
    buildingSlug?: string | null
}): string {
    const date = new Date().toISOString().slice(0, 10)
    const parts = ["pv"]
    if (opts.buildingSlug) {
        const slug = slugify(opts.buildingSlug)
        if (slug.length > 0) parts.push(slug)
    }
    parts.push(slugify(opts.chartSlug))
    parts.push(date)
    return `${parts.join("-")}.xlsx`
}

/**
 * Extrait l'AOA (array of arrays) en alignant les traces sur l'union de leurs valeurs X.
 * Les en-têtes de colonnes sont les `name` des traces Plotly (ou un fallback indexé).
 */
function buildAlignedAoa(
    traces: PlotlyTrace[],
    xAxisLabel: string,
    fallbackTraceName: string
): unknown[][] {
    const xKeys: (string | number)[] = []
    const seen = new Set<string>()
    for (const trace of traces) {
        const xs = Array.isArray(trace.x) ? trace.x : []
        for (const raw of xs) {
            const normalized = normalizeXValue(raw)
            const key = String(normalized)
            if (!seen.has(key)) {
                seen.add(key)
                xKeys.push(normalized)
            }
        }
    }

    const traceMaps = traces.map((trace) => {
        const map = new Map<string, unknown>()
        const xs = Array.isArray(trace.x) ? trace.x : []
        const ys = Array.isArray(trace.y) ? trace.y : []
        const len = Math.min(xs.length, ys.length)
        for (let i = 0; i < len; i++) {
            const key = String(normalizeXValue(xs[i]))
            map.set(key, normalizeYValue(ys[i]))
        }
        return map
    })

    const headers: unknown[] = [
        xAxisLabel,
        ...traces.map((trace, i) =>
            resolveTraceName(trace, fallbackTraceName, i)
        ),
    ]
    const rows: unknown[][] = [headers]
    for (const xKey of xKeys) {
        const row: unknown[] = [xKey]
        const k = String(xKey)
        for (const map of traceMaps) {
            row.push(map.has(k) ? (map.get(k) ?? null) : null)
        }
        rows.push(row)
    }
    return rows
}

/**
 * Composable d'export client-side des données de graphiques PV en Excel.
 * Génère un `.xlsx` à partir d'un payload Plotly (`graph.data` = traces) sans aucun appel backend.
 */
export function useChartExport() {
    /**
     * Télécharge les données d'un graphique Plotly comme fichier `.xlsx`.
     * Les noms de colonnes sont issus des `name` des traces Plotly (pas de `série_1`/`série_2`).
     */
    function downloadChartAsExcel(
        graph: PlotlyGraph | null | undefined,
        options: ChartExportOptions
    ): void {
        if (!graph || !Array.isArray(graph.data) || graph.data.length === 0) {
            return
        }
        const traces = graph.data as PlotlyTrace[]
        const xAxisLabel = options.xAxisLabel ?? "Date"
        const fallbackTraceName = options.fallbackTraceName ?? "Série"
        const sheetName = sanitizeSheetName(options.sheetName ?? "Données")

        const aoa = buildAlignedAoa(traces, xAxisLabel, fallbackTraceName)
        const wb = XLSX.utils.book_new()
        const sheet = XLSX.utils.aoa_to_sheet(aoa)
        XLSX.utils.book_append_sheet(wb, sheet, sheetName)

        XLSX.writeFile(wb, buildFilename(options))
    }

    /**
     * Télécharge un AOA arbitraire comme fichier `.xlsx`. Utiliser pour des charts
     * dont la forme n'est pas une série temporelle multi-trace alignable
     * (ex. comparateur scatter avec étiquettes par point).
     */
    function downloadRowsAsExcel(
        rows: unknown[][],
        options: ChartExportRowsOptions
    ): void {
        if (!Array.isArray(rows) || rows.length === 0) return
        const sheetName = sanitizeSheetName(options.sheetName ?? "Données")
        const wb = XLSX.utils.book_new()
        const sheet = XLSX.utils.aoa_to_sheet(rows)
        XLSX.utils.book_append_sheet(wb, sheet, sheetName)
        XLSX.writeFile(wb, buildFilename(options))
    }

    /**
     * Télécharge un classeur multi-onglets composé d'AOA et/ou de graphiques Plotly.
     * Onglets absents (graphique null, rows vides) sont silencieusement ignorés.
     */
    function downloadWorkbookAsExcel(
        sheets: WorkbookSheetSpec[],
        options: WorkbookExportOptions
    ): void {
        const wb = XLSX.utils.book_new()
        let appended = 0
        for (const spec of sheets) {
            const name = sanitizeSheetName(spec.name)
            if (spec.kind === "rows") {
                if (!Array.isArray(spec.rows) || spec.rows.length === 0)
                    continue
                XLSX.utils.book_append_sheet(
                    wb,
                    XLSX.utils.aoa_to_sheet(spec.rows),
                    name
                )
                appended++
                continue
            }
            const graph = spec.graph
            if (
                !graph ||
                !Array.isArray(graph.data) ||
                graph.data.length === 0
            ) {
                continue
            }
            const aoa = buildAlignedAoa(
                graph.data as PlotlyTrace[],
                spec.xAxisLabel ?? "Date",
                spec.fallbackTraceName ?? "Série"
            )
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), name)
            appended++
        }
        if (appended === 0) return
        XLSX.writeFile(wb, buildFilename(options))
    }

    return {
        downloadChartAsExcel,
        downloadRowsAsExcel,
        downloadWorkbookAsExcel,
    }
}
